import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  getClients,
  createClient,
  updateClient,
  deleteClient,
  bulkUpdateClients,
  getClientDisplayName,
  type Client,
} from '../../../services/api'
import { supabase } from '../../../lib/supabase'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function DashboardStudents() {
  const [loading, setLoading] = useState(true)
  const [students, setStudents] = useState<(Client & {
    payments_count?: number
    bookings_count?: number
    sessions_count?: number
    sessions_remaining?: number
  })[]>([])
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set())
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingStudent, setEditingStudent] = useState<Client | null>(null)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'active' as 'active' | 'inactive' | 'archived',
    notes: '',
    service_preferences: {} as Record<string, any>,
  })

  useEffect(() => {
    loadData()
  }, [statusFilter])

  const loadData = async () => {
    setLoading(true)
    try {
      const filters: any = {}
      if (statusFilter !== 'all') filters.status = statusFilter
      if (searchTerm) filters.search = searchTerm

      const clientsData = await getClients(filters)
      
      // Get counts for each client
      const clientsWithCounts = await Promise.all(
        clientsData.map(async (client) => {
          const [paymentsCount, bookingsCount, sessionsCount] = await Promise.all([
            supabase
              .from('payments')
              .select('id, metadata', { count: 'exact', head: true })
              .eq('client_id', client.id)
              .eq('status', 'completed'),
            supabase
              .from('bookings')
              .select('id', { count: 'exact', head: true })
              .eq('client_id', client.id),
            supabase
              .from('client_sessions')
              .select('id', { count: 'exact', head: true })
              .eq('client_id', client.id),
          ])

          // Calculate total sessions remaining from payments
          const { data: payments } = await supabase
            .from('payments')
            .select('metadata')
            .eq('client_id', client.id)
            .eq('status', 'completed')

          const totalSessionsRemaining = payments?.reduce((total, payment) => {
            const sessions = payment.metadata?.sessionsRemaining || 0
            return total + sessions
          }, 0) || 0

          return {
            ...client,
            payments_count: paymentsCount.count || 0,
            bookings_count: bookingsCount.count || 0,
            sessions_count: sessionsCount.count || 0,
            sessions_remaining: totalSessionsRemaining,
          }
        })
      )

      setStudents(clientsWithCounts)
    } catch (error) {
      console.error('Error loading students:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingStudent(null)
    setFormData({
      name: '',
      email: '',
      phone: '',
      status: 'active',
      notes: '',
      service_preferences: {},
    })
    setIsFormOpen(true)
  }

  const handleEdit = (student: Client) => {
    setEditingStudent(student)
    setFormData({
      name: getClientDisplayName(student),
      email: student.email,
      phone: student.phone || '',
      status: student.status,
      notes: student.notes || '',
      service_preferences: student.service_preferences || {},
    })
    setIsFormOpen(true)
  }

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      alert('Please fill in required fields (Name and Email)')
      return
    }

    setSaving(true)
    try {
      const studentData: any = {
        ...formData,
        phone: formData.phone || null,
        notes: formData.notes || null,
      }

      if (editingStudent) {
        await updateClient(editingStudent.id, studentData)
      } else {
        await createClient(studentData)
      }

      setIsFormOpen(false)
      loadData()
    } catch (error: any) {
      alert(error.message || 'Failed to save student')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this student? This action cannot be undone.')) return

    try {
      await deleteClient(id)
      loadData()
    } catch (error) {
      alert('Failed to delete student')
    }
  }

  const handleBulkAction = async (action: 'delete' | 'status', value?: string) => {
    if (selectedStudents.size === 0) {
      alert('Please select at least one student')
      return
    }

    if (action === 'delete') {
      if (!confirm(`Are you sure you want to delete ${selectedStudents.size} student(s)?`)) return
      for (const id of selectedStudents) {
        await deleteClient(id)
      }
    } else if (action === 'status' && value) {
      await bulkUpdateClients(Array.from(selectedStudents), { status: value as any })
    }

    setSelectedStudents(new Set())
    loadData()
  }

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedStudents)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedStudents(newSelected)
  }

  const toggleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set())
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.id)))
    }
  }

  const filteredStudents = students.filter(student => {
    const displayName = getClientDisplayName(student);
    const matchesSearch = !searchTerm || 
      displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.first_name && student.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (student.last_name && student.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.phone?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 dark:text-green-400'
      case 'inactive':
        return 'text-yellow-600 dark:text-yellow-400'
      case 'archived':
        return 'text-red-600 dark:text-red-400'
      default:
        return 'text-text-primary/70'
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">⏳</div>
        <p className="text-text-primary/70">Loading students...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-text-primary mb-1">Students</h3>
          <p className="text-sm text-text-primary/70">Manage client/student records</p>
        </div>
        <motion.button
          onClick={handleCreate}
          className="px-4 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-semibold text-text-primary neu-hover bg-accent/10 hover:bg-accent/20 transition-colors"
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          transition={springConfig}
        >
          + New Student
        </motion.button>
      </div>

      {/* Filters and Search */}
      <div className="neu p-4 rounded-2xl space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedStudents.size > 0 && (
          <div className="flex items-center gap-2 pt-2 border-t border-shadow-dark/10">
            <span className="text-sm text-text-primary/70">
              {selectedStudents.size} selected
            </span>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleBulkAction('status', e.target.value)
                  e.target.value = ''
                }
              }}
              className="px-3 py-1.5 neu rounded-lg text-sm text-text-primary bg-background focus:outline-none"
            >
              <option value="">Bulk Status Update</option>
              <option value="active">Set to Active</option>
              <option value="inactive">Set to Inactive</option>
              <option value="archived">Set to Archived</option>
            </select>
            <motion.button
              onClick={() => handleBulkAction('delete')}
              className="px-3 py-1.5 neu rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Delete Selected
            </motion.button>
          </div>
        )}
      </div>

      {/* Students List */}
      {filteredStudents.length === 0 ? (
        <div className="neu p-12 rounded-2xl text-center">
          <p className="text-text-primary/70">No students found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="neu rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background/50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedStudents.size === filteredStudents.length && filteredStudents.length > 0}
                        onChange={toggleSelectAll}
                        className="neu rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Phone</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Stats</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr
                      key={student.id}
                      className="border-t border-shadow-dark/10 hover:bg-background/30"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedStudents.has(student.id)}
                          onChange={() => toggleSelect(student.id)}
                          className="neu rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary font-medium">
                        {getClientDisplayName(student)}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        {student.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary/70">
                        {student.phone || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary/70">
                        <div className="flex flex-col gap-1">
                          <span>Payments: {student.payments_count || 0}</span>
                          <span>Bookings: {student.bookings_count || 0}</span>
                          <span>Sessions: {student.sessions_count || 0}</span>
                          {student.sessions_remaining !== undefined && (
                            <span className="text-accent font-medium">
                              Credits: {student.sessions_remaining}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(student.status)}`}>
                          {student.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(student)}
                            className="text-accent hover:text-accent-secondary text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(student.id)}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsFormOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9 }}
              className="neu rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-semibold mb-4 text-text-primary">
                {editingStudent ? 'Edit Student' : 'New Student'}
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-text-primary/70">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                    placeholder="Internal notes about this student..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <motion.button
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2.5 neu rounded-xl font-medium text-text-primary/70 hover:text-text-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2.5 neu rounded-xl font-semibold text-text-primary neu-hover bg-accent/10 hover:bg-accent/20 disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

